import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import type { Dayjs } from 'dayjs'
import {
	animate,
	motion,
	useMotionValue,
	useMotionValueEvent,
} from 'framer-motion'
import { useCallback, useMemo, useRef, useState } from 'react'

import { cn } from '~/lib/utils'

import { DayCol } from './day-col'

interface BookingWeekdayPickerProps {
	value: string
	onChange: (day: string) => void
	min?: string
	max?: string
	className?: string
}

export const DAYS_TO_DISPLAY = 7

export const BookingWeekdayPicker = ({
	value,
	onChange,
	min,
	max,
	className,
}: BookingWeekdayPickerProps) => {
	const x = useMotionValue(0)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const [xState, setXState] = useState(x.get())

	const days = useMemo(() => {
		const offset = 6
		const valueformated = dateTimeUtils(value)

		const previousDays = Array.from({ length: offset }, (_, i) =>
			valueformated.subtract(offset - i, 'day'),
		)

		const nextDays = Array.from({ length: offset }, (_, i) =>
			valueformated.add(i + 1, 'day'),
		)

		return [...previousDays, valueformated, ...nextDays]
	}, [value])

	useMotionValueEvent(x, 'change', setXState)

	const handleClickDay = useCallback(
		(day: Dayjs, index: number) => {
			const middleDayIndex = Math.floor(days.length / 2)

			if (index === middleDayIndex || !wrapperRef.current) {
				return
			}

			animate(
				x,
				(wrapperRef.current?.offsetWidth / DAYS_TO_DISPLAY) *
					(middleDayIndex - index),
				{
					onComplete() {
						x.set(0, false)
						onChange(day.format('YYYY-MM-DD'))
					},
				},
			)
		},
		[days.length, onChange, x],
	)

	return (
		<div className={cn('overflow-hidden', className)}>
			<motion.div
				ref={wrapperRef}
				className='inline-flex w-full flex-nowrap justify-center'
				style={{ x: xState }}
			>
				{days.map((day, index) => (
					<DayCol
						key={day.toString()}
						disabled={
							(!!min && day.isBefore(min, 'day')) ||
							(!!max && day.isAfter(max, 'day'))
						}
						day={day}
						selectedDay={dateTimeUtils(value)}
						onClick={() => handleClickDay(day, index)}
					/>
				))}
			</motion.div>
		</div>
	)
}
